import type { JSX } from 'react';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';
import { Spinner } from './ProgressBar';

/**
 * The three buttons this app has — a port of
 * `android/.../ui/components/NeonButton.kt`, one export per Kotlin composable.
 *
 * They share a silhouette and differ only in fill, so all three are one-line
 * wrappers over {@link BaseButton} and a base class. That is the same structure
 * the Kotlin uses, and it is the reason the three can never drift apart in
 * padding, in how they wrap, or in the `type="button"` below. The three names
 * stay because they mirror the Kotlin API a reader is likely to have open beside
 * this file.
 *
 * ### The gradient does not end on neon cyan, and must not be "fixed" to
 *
 * `--cta-gradient-end` is `#0b7c99`, a teal, not the `#00f0ff` the design brief
 * asked for. From Color.kt: **white on neon cyan measures 1.41:1**, so a ramp
 * that ended on the real neon would leave the right-hand third of every primary
 * button carrying an unreadable label. This ramp bottoms out at 4.77:1 for
 * white across its whole length. The neon is put back around the pill instead —
 * the 1.5px cyan stroke and the outer bloom — where it is decoration and no text
 * ever sits on it. Android's `DecorContrastTest` measures this; nothing on this
 * side can, so the value is inherited rather than re-derived.
 *
 * ### Height is a minimum, never a fixed value
 *
 * `min-height: var(--touch-target)` and a label that wraps to three lines
 * (`.neon-btn__label`, `-webkit-line-clamp: 3`). Android's comment is worth
 * repeating: Portuguese runs about a third longer than the Vietnamese these
 * labels were laid out against, and a truncated verb leaves a button that no
 * longer says what it does. A fixed height clips instead of growing.
 */

/** What the three exports differ by, and the only thing they differ by. */
type ButtonVariant = 'filled' | 'outlined' | 'danger';

/** The filled pill is `.neon-btn` itself; the other two add one modifier. */
const VARIANT_CLASS: Record<ButtonVariant, string | null> = {
  filled: null,
  outlined: 'neon-btn--outlined',
  danger: 'neon-btn--danger',
};

/** Everything all three buttons take. */
interface ButtonProps {
  text: string;
  onClick(): void;
  disabled?: boolean;
  icon?: IconName;
  className?: string;
}

/**
 * The primary action: a full-width pill filled with the blue-to-cyan gradient,
 * outlined in neon and sitting in its own bloom.
 *
 * `loading` swaps the content for a spinner and disables the button, exactly as
 * the Kotlin's `enabled = enabled && !loading` does — a second tap on a key
 * that is already being verified would fire a second request against the same
 * backend.
 */
export function NeonButton(props: ButtonProps & { loading?: boolean }): JSX.Element {
  return <BaseButton variant="filled" {...props} />;
}

/**
 * The secondary action: same silhouette, no fill. Used for everything that is a
 * detour rather than the thing you came to do — "view results", "stop".
 *
 * No `loading`, matching the Kotlin: nothing behind a secondary action here is
 * slow enough to need one, and a spinner on a detour reads as the detour having
 * become the main event. That is why it is absent from `ButtonProps` rather than
 * merely unused.
 */
export function NeonOutlinedButton(props: ButtonProps): JSX.Element {
  return <BaseButton variant="outlined" {...props} />;
}

/**
 * The destructive action: the outlined silhouette again, in error red.
 *
 * Deliberately not the filled variant. Android's comment applies unchanged, and
 * on this platform it applies harder: removing the device key here cannot be
 * undone from inside the app at all, because the key lives in the WebClip
 * icon's URL and only a new install link replaces it (SPEC §13). A gradient
 * pill would make that compete with Start on the dashboard.
 */
export function NeonDangerButton(props: ButtonProps): JSX.Element {
  return <BaseButton variant="danger" {...props} />;
}

/** The one button. The three exports above choose its `variant` and nothing else. */
function BaseButton({
  variant,
  text,
  onClick,
  disabled = false,
  loading = false,
  icon,
  className,
}: ButtonProps & { variant: ButtonVariant; loading?: boolean }): JSX.Element {
  return (
    <button
      // Always explicit. A bare <button> inside a <form> defaults to `submit`,
      // and a submit in a WebClip navigates the page — which unmounts the app
      // and kills any cycle in flight. There is no form here today; this is the
      // guard against one being added later.
      type="button"
      className={cx('neon-btn', VARIANT_CLASS[variant], className)}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      // The visible label is gone while the spinner is up, so the accessible
      // name has to be carried explicitly or VoiceOver announces an unnamed
      // button on the one screen where nothing else is on the page.
      aria-label={loading ? text : undefined}
    >
      <ButtonContent text={text} loading={loading} icon={icon} />
    </button>
  );
}

/**
 * The shared interior. A fragment, not a wrapper element: `.neon-btn` is the
 * flex container with the `gap`, so the icon and the label have to be its own
 * children. Wrapping them in a `<span>` would collapse the gap and re-centre
 * the pair against the padding rather than the button.
 */
function ButtonContent({
  text,
  loading,
  icon,
}: {
  text: string;
  loading: boolean;
  icon?: IconName;
}): JSX.Element {
  // Content is replaced, not hidden. Keeping the label mounted and overlaying a
  // spinner would let the button keep its old width while the label re-wraps
  // underneath — Android returns early here for the same reason.
  if (loading) return <Spinner />;
  return (
    <>
      {/* 18px, Android's `IconSize` in NeonButton.kt — one notch below the
          20px row default, because it sits beside text rather than leading a row. */}
      {icon === undefined ? null : <Icon name={icon} size={18} className="neon-btn__icon" />}
      <span className="neon-btn__label t-label-large clamp-3">{text}</span>
    </>
  );
}
